import { AnchorHTMLAttributes, HTMLAttributes, LiHTMLAttributes } from "react";
import { Link, LinkProps } from "react-router-dom";
import { cn } from "@app/lib/utils";
import { Tooltip } from "../helpers";
import { Text } from "../text";
import { cardInteractiveClassName, cardSurfaceClassName } from "./Card";
import { pageGridClassName } from "./Page";

function BoxRoot({ className, ...rest }: HTMLAttributes<HTMLUListElement>) {
  return (
    <ul className={cn("grid", className || pageGridClassName)} {...rest} />
  );
}

function Item({ className, ...props }: LiHTMLAttributes<HTMLLIElement>) {
  return (
    <li
      className={cn(
        cardSurfaceClassName,
        cardInteractiveClassName,
        "relative col-span-1 flex h-full min-w-0 flex-col overflow-hidden",
        className,
      )}
      {...props}
    />
  );
}

function Avatar({ image, icon }: { image?: string; icon?: React.ReactNode }) {
  return (
    <div className="flex justify-center">
      {image ? (
        <img
          className="h-16 w-16 flex-shrink-0 rounded-full object-cover"
          src={image}
          alt=""
        />
      ) : icon ? (
        icon
      ) : undefined}
    </div>
  );
}

function Title(props: HTMLAttributes<HTMLHeadElement>) {
  return (
    <h3
      className="mt-4 line-clamp-2 text-center text-base font-bold leading-6 text-foreground"
      {...props}
    />
  );
}

function Subtitle(props: HTMLAttributes<HTMLDivElement>) {
  return (
    <Text
      variant="gray"
      className="mt-1 line-clamp-2 text-center text-sm leading-5"
      {...props}
    />
  );
}

function FooterButton({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("mt-auto flex w-full divide-s divide-border", className)}
      {...props}
    />
  );
}

function FooterButtonItem({
  icon,
  title,
  ...props
}: { icon: React.ReactNode; title: string } & (
  | AnchorHTMLAttributes<HTMLAnchorElement>
  | LinkProps
)) {
  const Component: any = (props as any).to
    ? Link
    : (props as any).href
      ? "a"
      : "button";

  return (
    <div className="flex w-0 flex-1">
      <Tooltip tooltip={title}>
        <Component
          className="relative inline-flex h-12 w-0 flex-1 items-center justify-center font-semibold text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
          {...props}
        >
          <Text variant="gray">
            <span aria-hidden="true">{icon}</span>
          </Text>
        </Component>
      </Tooltip>
    </div>
  );
}

export const Box = Object.assign(BoxRoot, {
  Item,
  FooterButton,
  FooterButtonItem,
  Avatar,
  Title,
  Subtitle,
});
